import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Search, Music, Mic2 } from "lucide-react";
import { Input } from "../components/ui/input";

export default function ChantsPage() {
  const [chants, setChants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    axios.get(`${API}/chants`)
      .then(res => setChants(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = ["all", ...new Set(chants.map(c => c.category))];

  const filtered = chants.filter(c => {
    const matchesCategory = activeCategory === "all" || c.category === activeCategory;
    const matchesSearch = !search ||
      c.title.toLowerCase().includes(search.toLowerCase()) ||
      c.lyrics.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="chants-page">
      <SEO title="West Ham Chants" path="/chants" description="West Ham United chants and songs. Learn the lyrics to classic Hammers terrace anthems including I'm Forever Blowing Bubbles and more." />
      <Navbar />

      <div className="bg-[#7C2C3B] py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            WEST HAM CHANTS & SONGS
          </h1>
          <p className="text-white/80">The soundtrack of the terraces — every song the Hammers faithful sing</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search chants by title or lyrics..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-12 text-base"
            data-testid="chants-search"
          />
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="chants-category-filter">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors capitalize ${
                activeCategory === cat
                  ? "bg-[#7C2C3B] text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:border-[#7C2C3B]"
              }`}
              data-testid={`chants-filter-${cat}`}
            >
              {cat === "all" ? `All (${chants.length})` : `${cat} (${chants.filter(c => c.category === cat).length})`}
            </button>
          ))}
        </div>

        {/* Chants Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Mic2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No chants found matching your search.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {filtered.map((chant) => (
              <Link
                key={chant.id}
                to={chant.slug ? `/chants/${chant.slug}` : "#"}
                className="block bg-white rounded-xl border border-slate-200 hover:border-[#7C2C3B]/50 hover:shadow-sm transition-all"
                data-testid={`chant-card-${chant.id}`}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#7C2C3B]/10 flex items-center justify-center flex-shrink-0">
                        <Music className="w-5 h-5 text-[#7C2C3B]" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
                          {chant.title.toUpperCase()}
                        </h3>
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">
                          {chant.category}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 line-clamp-2 mt-1">
                    {chant.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-8 text-center text-sm text-slate-400">
          {filtered.length} of {chants.length} chants shown
        </div>
      </div>

      <Footer />
    </div>
  );
}
